import { Component, Input, Output, EventEmitter, SimpleChanges, HostListener } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { environment } from '@env/environment';

@Component({
    selector: 'app-double-dropdown',
    templateUrl: './double-dropdown.component.html',
    styleUrls: ['./double-dropdown.component.scss']
})
export class DoubleDropdownComponent {
    @Input() type: string = '';
    @Input() id: string = '';
    @Input() labelSingular: string = '';
    @Input() labelPlural: string = '';
    @Input() placeholder: string = '';
    @Input() form: any;
    @Input() formSelected: string;
    @Input() formType: string;
    @Input() treeData: any[] = [];
    @Input() removeItem: any;
    @Input() isDisabled: boolean = false;
    @Input() isLoading: boolean = true;
    @Input() isLoadingMore: boolean = false;
    @Input() hasMoreData: boolean = false;
    @Input() position:
        | 'bottom-left' | 'bottom-right'
        | 'top-left' | 'top-right'
        | 'left-top' | 'left-bottom'
        | 'right-top' | 'right-bottom' = 'bottom-left';
    @Input() styleDropdown: string;
    @Input() filterBy: string;
    @Input() isReadOnly: boolean = false;

    @Output() loadMoreEmit = new EventEmitter<any>();

    typeOptions: any[];
    showDropdown = false;
    selectedKeys = new Set<string>();
    parentMap = new Map<any, any>();
    baseUrl = environment.BASE_PATH;
    dropdownPositionClass = '';
    searchKeyword: string = '';
    filterTreeData: any[] = [];
    isAllChecked: boolean = false;
    debounceTimer: any;

    ngOnInit() {
        this.typeOptions = [
            {
                name: 'COMMON.INCLUDE',
                key: 'INCLUDE',
            },
            {
                name: 'COMMON.EXCLUDE',
                key: 'EXCLUDE',
            },
        ];

        if (!this.form.value?.[this.formType]) {
            this.form.patchValue({
                [this.formType]: this.typeOptions[0]
            })
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['treeData'] && changes['treeData'].currentValue) {
            if (this.treeData?.length) {
                this.buildParentMap(this.treeData);
                this.isAllChecked = this.isAllSelected();
            }
        }
        if (changes['removeItem'] && changes['removeItem'].currentValue) {
            this.onRemoveItem(this.removeItem);
        }
        if (changes['position'] && changes['position'].currentValue) {
            this.updateDropdownPositionClass();
        }
    }

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    buildParentMap(nodes: any[], parent: any = null) {
        for (let node of nodes) {
            if (parent) {
                this.parentMap.set(node, parent);
            }
            if (node.children?.length) {
                this.buildParentMap(node.children, node);
            }
        }
    }

    toggleDropdown() {
        this.showDropdown = !this.showDropdown;
    }

    toggleAll(event: any) {
        this.form?.get(this.formSelected)?.markAsTouched();

        this.treeData.forEach((node) => {
            node.isChecked = this.isAllChecked;
            node.isIndeterminate = false;
            if (node.children?.length) {
                this.toggleChildren(node, !this.isAllChecked);
            }
        });
        this.updateSelectedItems();
    }

    toggleItem(item: any, parent?: any) {
        this.form?.get(this.formSelected)?.markAsTouched();
        const isChecked = item.isChecked;
        // If item has children toggle all tree children
        if (item.children?.length) {
            this.toggleChildren(item, isChecked);
        }
        // Update checked item
        item.isChecked = !item.isChecked;
        item.isIndeterminate = false;
        // After toggle, update parent node
        if (parent) {
            this.updateParentNode(parent);
        }
        this.updateSelectedItems();
        this.isAllChecked = this.isAllSelected();
    }

    // Toggle all children tree
    toggleChildren(item: any, select: boolean) {
        item.children?.forEach((child: any) => {
            if (select) {
                child.isChecked = false;
                child.isIndeterminate = false;
            } else {
                child.isChecked = true;
                child.isIndeterminate = false;
            }
            if (child.children?.length) {
                this.toggleChildren(child, select);
            }
        });
    }

    // Update parent node checked
    updateParentNode(node: any) {
        const children = node.children || [];
        const allSelected = children.every((c: any) => c.isChecked);
        const anySelected = children.some((c: any) => c.isChecked || c.isIndeterminate);

        if (allSelected) {
            node.isChecked = true;
            node.isIndeterminate = false;
        } else if (anySelected) {
            node.isChecked = false;
            node.isIndeterminate = true;
        } else {
            node.isChecked = false;
            node.isIndeterminate = false;
        }

        const parent = this.parentMap.get(node);
        if (parent) this.updateParentNode(parent);
    }

    isAllSelected(): boolean {
        const isAllSelected = this.treeData.every(node => node.isChecked);
        return isAllSelected;
    }

    onTypeChange() {
        if (this.form?.[this.formType]?.key === 'EXCLUDE') {
            const allSelected = this.treeData.every(node => node.isChecked);

            if (allSelected) {
                this.treeData.forEach(node => {
                    node.isChecked = false;
                    node.isIndeterminate = false;
                    this.toggleChildren(node, true);
                });
                this.updateSelectedItems();
            }
        }
    }

    updateSelectedItems() {
        const selected: any[] = [];

        const collectChecked = (nodes: any[]) => {
            for (const node of nodes) {
                if (node.children?.length) {
                    collectChecked(node.children);
                } else if (node.isChecked) {
                    selected.push(node);
                }
            }
        };

        collectChecked(this.treeData);
        this.form.patchValue({
            [this.formSelected]: selected
        });
    }

    @HostListener('document:click', ['$event'])
    onOutsideClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (!target.closest('#' + this.id)) {
            if (this.showDropdown) this.showDropdown = false;
        }
    }

    toggleCollapse(item: any) {
        item.isCollapsed = !item.isCollapsed;
    }

    replaceWithDefaultImage(event: any): void {
        event.target.src = 'assets/images/user-management/default-image.jpg';
    }

    onRemoveItem(item: any) {
        // Update checked item and parent of node
        item.isChecked = false;
        item.isIndeterminate = false;
        const parent = this.parentMap.get(item);
        if (parent) this.updateParentNode(parent);
        this.isAllChecked = this.isAllSelected();
    }

    onScrollDropdown(event: Event): void {
        const scrollableDiv = document.querySelector('#dropdown_wrapper_' + this.id);
        if (!scrollableDiv) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollableDiv;
        if ((scrollHeight - scrollTop - clientHeight < 50) && this.hasMoreData && !this.isLoadingMore) {
            this.loadMoreEmit.emit();
        }
    }

    private updateDropdownPositionClass(): void {
        switch (this.position) {
            case 'bottom-left':
                this.dropdownPositionClass = 'top-full left-0';
                break;
            case 'bottom-right':
                this.dropdownPositionClass = 'top-full right-0';
                break;
            case 'top-left':
                this.dropdownPositionClass = 'bottom-full left-0';
                break;
            case 'top-right':
                this.dropdownPositionClass = 'bottom-full right-0';
                break;
            case 'left-top':
                this.dropdownPositionClass = 'right-full top-0';
                break;
            case 'left-bottom':
                this.dropdownPositionClass = 'right-full bottom-0';
                break;
            case 'right-top':
                this.dropdownPositionClass = 'left-full top-0';
                break;
            case 'right-bottom':
                this.dropdownPositionClass = 'left-full bottom-0';
                break;
            default:
                this.dropdownPositionClass = 'top-full left-0';
        }
    }

    onKeywordChange(value: string) {
        this.searchKeyword = value;

        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }

        this.isLoading = true;
        this.debounceTimer = setTimeout(() => {
            this.onSearch();
        }, 300);
    }

    onSearch() {
        const keyword = this.searchKeyword?.trim().toLowerCase();
        if (!keyword) {
            this.filterTreeData = [];
            this.isLoading = false;
            return;
        }

        this.filterTreeData = this.filterTree(this.treeData, keyword);
        this.isLoading = false;
    }

    filterTree(nodes: any[], keyword: string): any[] {
        const lowerKeyword = keyword.toLowerCase();

        return nodes
            .map(node => {
                const labelMatch = node.label?.toLowerCase().includes(lowerKeyword);
                let codeMatch;
                if (this.filterBy) {
                    codeMatch = node?.[this.filterBy]?.toLowerCase().includes(lowerKeyword);
                }

                const isMatch = labelMatch || codeMatch;

                if (isMatch) {
                    if (node.children) {
                        node.filterChildren = node.children;
                    }
                    return node;
                }

                const filteredChildren = node.children ? this.filterTree(node.children, keyword) : [];

                if (filteredChildren.length > 0) {
                    node.filterChildren = filteredChildren;
                    return node;
                }

                return null;
            })
            .filter(Boolean);
    }
}
